import { useState, type ReactNode } from 'react';
import { Button } from '../../catalyst/button';

/**
 * DR-2426: expensive visuals render behind an explicit click when their caller's workload
 * policy crosses its threshold. Each caller supplies the relevant signal (classification leaves
 * or lineage leaves plus reconstructed entities), so unrelated document counts do not gate a
 * visual. The opt-in is local and non-persisted; smaller workloads render directly. A visual
 * that needs paginated geometry waits for its shared data to finish before mounting.
 */
export function HeavyVisualGate({
  isHuge,
  label,
  children,
  loading = false,
  error,
  onRequestRender,
  onRetry,
}: {
  isHuge: boolean;
  /** What the user is choosing to render, e.g. "the confidence histogram". */
  label: string;
  children: ReactNode;
  /** Prevent mounting children until pre-render data is complete. */
  loading?: boolean;
  error?: Error;
  /** Starts any asynchronous preparation needed before children can mount. */
  onRequestRender?: () => void;
  /** Restarts failed preparation from a clean data generation. */
  onRetry?: () => void;
}) {
  const [renderRequested, setRenderRequested] = useState(false);

  const requestRender = () => {
    setRenderRequested(true);
    onRequestRender?.();
  };

  const retry = () => {
    setRenderRequested(true);
    onRetry?.();
  };

  if (!isHuge && !loading && !error) return <>{children}</>;

  if (isHuge && !renderRequested) {
    return <GateButton label={label} onClick={requestRender} />;
  }

  if (loading) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
        <span className="font-medium">Loading data for {label}</span>
        <span className="text-xs text-gray-500 dark:text-zinc-400">This may take a moment</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-red-300 bg-red-50 px-4 py-6 text-center text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        <span className="font-medium">Could not load data for {label}</span>
        <Button plain onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}

function GateButton({ label, onClick }: { label: string; onClick: () => void }) {
  // Catalyst Button (plain) carrying the dashed-box affordance via buttonClassName, which
  // twMerge applies last so it overrides the primitive's inline-flex/rounded/border defaults.
  return (
    <Button
      plain
      onClick={onClick}
      buttonClassName="flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300"
    >
      <span className="font-medium">Large document — click to render {label}</span>
      <span className="text-xs text-gray-500 dark:text-zinc-400">May be slow</span>
    </Button>
  );
}
